import { Form, Alert } from 'antd';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMemo } from 'react';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { ControlledFormItem, Button } from '@/shared/components/ui/atoms';
import type { FederatedLoginFormData, FederatedLoginFormProps } from './types';
import './FederatedLoginForm.css';

/**
 * FederatedLoginForm Organism
 *
 * "Acessar com SSO" form — collects the corporate e-mail of the organization
 * the user wants to sign in to. The page resolves the e-mail domain to an
 * IAM-SAML registrationId and redirects the browser to the organization's IdP.
 *
 * ## Features
 * - **Email Validation**: Validates e-mail format with Zod
 * - **Federation Check**: Submits the e-mail for DOMAIN x registrationId lookup
 * - **Loading State**: Disables form during the check / redirect
 * - **Error Feedback**: Shows API / non-federated-domain errors
 *
 * @example
 * ```tsx
 * <FederatedLoginForm
 *   onSubmit={handleFederatedLogin}
 *   loading={isPending}
 *   error={errorMessage}
 * />
 * ```
 *
 * @param props - FederatedLoginForm component props
 * @returns A complete federated login form with validation
 * @since 1.0.0
 * @category Organisms
 */
const FederatedLoginForm: React.FC<FederatedLoginFormProps> = ({
  onSubmit,
  loading = false,
  error = null,
  defaultEmail = '',
  redirecting = false,
  accessNotGranted = false,
}) => {
  const { t } = useLanguage();

  const federatedLoginSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(3, t('Invalid email. Try again.'))
          .email(t('Invalid email. Try again.')),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid, errors: fieldErrors },
  } = useForm<FederatedLoginFormData>({
    resolver: zodResolver(federatedLoginSchema),
    mode: 'onChange',
    defaultValues: {
      email: defaultEmail,
    },
  });

  const email = useWatch({ control, name: 'email' });

  const isSubmitDisabled = useMemo(() => {
    return loading || isSubmitting || !email || email.length < 3 || !isValid;
  }, [loading, isSubmitting, email, isValid]);

  const handleFormSubmit = async (data: FederatedLoginFormData) => {
    await onSubmit(data);
  };

  return (
    <div className="auth-page-container">
      <div className="auth-container">
        <div className="federated-login-form-wrapper">
          <div className="federated-login-form-header">
            <div className="federated-login-form-header-content">
              <p className="federated-login-form-header-small">
                {t('Platform Name')}
              </p>
              <h1 className="federated-login-form-header-title">
                {t('Access with SSO')}
              </h1>
            </div>

            <div className="federated-login-form-header-description">
              <p>
                {t('Enter the email of the organization you want to access')}
              </p>
            </div>
          </div>

          {redirecting && (
            <Alert
              message={t(
                "You will be redirected to your company's sign-in environment..."
              )}
              type="info"
              showIcon
              role="status"
              aria-live="polite"
            />
          )}

          {accessNotGranted && (
            <Alert
              message={
                <span>
                  <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                    {t('Access denied:')}
                  </span>{' '}
                  {t('You do not have permission to access this platform')}
                </span>
              }
              type="error"
              showIcon
            />
          )}

          {error && (
            <Alert
              message={
                <span>
                  <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                    {t('Attention!')}
                  </span>{' '}
                  {error}
                </span>
              }
              type="error"
              showIcon
              closable
            />
          )}

          <Form
            layout="vertical"
            onFinish={handleSubmit(handleFormSubmit)}
            noValidate
            className="federated-login-form"
          >
            <ControlledFormItem
              name="email"
              control={control as never}
              errors={
                fieldErrors.email
                  ? fieldErrors
                  : error
                    ? { email: { message: '' } }
                    : {}
              }
              label={t('Email')}
              inputType="text"
              inputProps={{
                placeholder: t('Type your email here'),
                autoComplete: 'email',
              }}
            />

            <Form.Item className="federated-login-form-submit-item">
              <Button
                htmlType="submit"
                variant="primary"
                size="lg"
                disabled={isSubmitDisabled}
              >
                {t('Continue')}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default FederatedLoginForm;
